import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart, Line
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

// Theme-aware colors hook
const useThemeColors = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.body.classList.contains('dark-mode');
  });

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.body.classList.contains('dark-mode'));
    };
    
    // Check on mount
    checkDarkMode();
    
    // Use MutationObserver to watch for class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    // Also poll as backup (faster interval)
    const interval = setInterval(checkDarkMode, 100);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return {
    isDark,
    cardBg: isDark ? '#1e293b' : '#f8f9fa',
    textPrimary: isDark ? '#e2e8f0' : '#333333',
    textSecondary: isDark ? '#94a3b8' : '#666666',
    textMuted: isDark ? '#64748b' : '#999999',
    borderColor: isDark ? '#374151' : '#e5e7eb',
    chartGrid: isDark ? '#374151' : '#e5e7eb',
    chartText: isDark ? '#e2e8f0' : '#374151',
    tooltipBg: isDark ? '#1e293b' : '#ffffff',
    tableBg: isDark ? '#1e293b' : '#ffffff',
    tableHeaderBg: isDark ? '#334155' : '#f3f4f6',
    tableHoverBg: isDark ? '#374151' : '#f9fafb',
    // For pie chart labels
    labelColor: isDark ? '#e2e8f0' : '#333333',
  };
};

// WhatsApp SVG Icon Component
const WhatsAppIcon = ({ size = 24 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Insights = () => {
  const { section } = useParams(); // Get section from URL
  const { user, gym } = useAuth();
  const isAdmin = user?.role === 'admin';
  const themeColors = useThemeColors();
  
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Map URL section to activeSection
  const activeSection = section || 'members';
  
  // Filters - All hooks must be before any conditional returns
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedTenure, setSelectedTenure] = useState('');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [renewalFilter, setRenewalFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  // Bulk selection state
  const [selectedRenewalMembers, setSelectedRenewalMembers] = useState([]);
  const [selectedActionMembers, setSelectedActionMembers] = useState([]);
  const [messageSettings, setMessageSettings] = useState({
    whatsappTemplate: 'Hi {name}, your gym membership is {status}. Please renew to continue your fitness journey!',
    emailSubject: 'Gym Membership Renewal Reminder',
    emailTemplate: 'Hi {name},\n\nYour gym membership is {status}.\n\nPlease renew to continue your fitness journey!\n\nThank you,\n{gymName}'
  });

  // Follow-up tracker state
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpMember, setFollowUpMember] = useState(null);
  const [followUpData, setFollowUpData] = useState({
    contactDate: new Date().toISOString().split('T')[0],
    contactMode: 'WhatsApp',
    notes: '',
    outcome: ''
  });
  const [followUps, setFollowUps] = useState([]);
  const [followUpStats, setFollowUpStats] = useState(null);
  const [showFollowUpHistory, setShowFollowUpHistory] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSettings();
    fetchFollowUps();
  }, []);

  const fetchFollowUps = async () => {
    try {
      const token = localStorage.getItem('token');
      const [followupsRes, statsRes] = await Promise.all([
        axios.get('/api/followups?limit=50', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/followups/stats', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFollowUps(followupsRes.data.followups || []);
      setFollowUpStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  };

  const handleLogFollowUp = (member) => {
    setFollowUpMember(member);
    setFollowUpData({
      contactDate: new Date().toISOString().split('T')[0],
      contactMode: 'WhatsApp',
      notes: '',
      outcome: ''
    });
    setShowFollowUpModal(true);
  };

  const submitFollowUp = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/followups', {
        memberId: followUpMember.id,
        ...followUpData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowFollowUpModal(false);
      fetchFollowUps();
      alert('Follow-up recorded successfully!');
    } catch (error) {
      alert('Failed to record follow-up: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.settings) {
        // Merge with defaults to ensure template fields always exist
        setMessageSettings(prev => ({
          ...prev,
          ...response.data.settings,
          // Ensure templates are never undefined
          whatsappTemplate: response.data.settings.whatsappTemplate || prev.whatsappTemplate,
          emailTemplate: response.data.settings.emailTemplate || prev.emailTemplate,
          emailSubject: response.data.settings.emailSubject || prev.emailSubject
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [membersRes, paymentsRes] = await Promise.all([
        axios.get('/api/members', { headers }),
        axios.get('/api/payments?limit=10000', { headers })  // Get all payments for insights calculations
      ]);
      
      setMembers(membersRes.data.members || []);
      setPayments(paymentsRes.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique years from data
  const availableYears = useMemo(() => {
    const years = new Set();
    years.add(new Date().getFullYear());
    members.forEach(m => {
      if (m.joiningDate) years.add(new Date(m.joiningDate).getFullYear());
    });
    payments.forEach(p => {
      if (p.paymentDate) years.add(new Date(p.paymentDate).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [members, payments]);

  const availableTenures = useMemo(() => {
    const tenures = new Set();
    members.forEach(m => {
      if (m.tenure) tenures.add(m.tenure);
    });
    return Array.from(tenures).sort((a, b) => a - b);
  }, [members]);

  const availablePaymentModes = useMemo(() => {
    const modes = new Set();
    payments.forEach(p => {
      if (p.modeOfPayment) modes.add(p.modeOfPayment);
    });
    return Array.from(modes);
  }, [payments]);

  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      const paymentDate = new Date(p.paymentDate);
      const yearMatch = !selectedYear || paymentDate.getFullYear() === parseInt(selectedYear);
      const monthMatch = !selectedMonth || (paymentDate.getMonth() + 1) === parseInt(selectedMonth);
      const modeMatch = !selectedPaymentMode || p.modeOfPayment === selectedPaymentMode;
      return yearMatch && monthMatch && modeMatch;
    });
  }, [payments, selectedYear, selectedMonth, selectedPaymentMode]);

  // eslint-disable-next-line no-unused-vars
  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const joinDate = new Date(m.joiningDate);
      const yearMatch = !selectedYear || joinDate.getFullYear() === parseInt(selectedYear);
      const monthMatch = !selectedMonth || (joinDate.getMonth() + 1) === parseInt(selectedMonth);
      const tenureMatch = !selectedTenure || m.tenure === parseInt(selectedTenure);
      return yearMatch && monthMatch && tenureMatch;
    });
  }, [members, selectedYear, selectedMonth, selectedTenure]);

  // Monthly Users Data
  const monthlyUsersData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = monthNames.map((name) => ({ month: name, members: 0 }));
    members.forEach(m => {
      if (m.joiningDate) {
        const date = new Date(m.joiningDate);
        if (date.getFullYear() === parseInt(selectedYear)) {
          data[date.getMonth()].members++;
        }
      }
    });
    return data;
  }, [members, selectedYear]);

  // Monthly Collection Data
  const monthlyCollectionData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = monthNames.map((name) => ({ month: name, collection: 0, transactions: 0 }));
    filteredPayments.forEach(p => {
      if (p.paymentDate) {
        const date = new Date(p.paymentDate);
        if (date.getFullYear() === parseInt(selectedYear)) {
          data[date.getMonth()].collection += parseFloat(p.amount) || 0;
          data[date.getMonth()].transactions++;
        }
      }
    });
    return data;
  }, [filteredPayments, selectedYear]);

  // Revenue by Payment Mode
  const revenueByPaymentMode = useMemo(() => {
    const modeData = {};
    filteredPayments.forEach(p => {
      const mode = p.modeOfPayment || 'Unknown';
      if (!modeData[mode]) {
        modeData[mode] = { name: mode, value: 0, count: 0 };
      }
      modeData[mode].value += parseFloat(p.amount) || 0;
      modeData[mode].count++;
    });
    return Object.values(modeData).sort((a, b) => b.value - a.value);
  }, [filteredPayments]);

  // Week over Week Revenue
  const weekOverWeekRevenue = useMemo(() => {
    const weeks = {};
    filteredPayments.forEach(p => {
      if (p.paymentDate) {
        const date = new Date(p.paymentDate);
        // Get week number
        const startOfYear = new Date(date.getFullYear(), 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        const weekKey = `W${weekNum}`;
        
        if (!weeks[weekKey]) {
          weeks[weekKey] = { week: weekKey, revenue: 0, transactions: 0, weekNum };
        }
        weeks[weekKey].revenue += parseFloat(p.amount) || 0;
        weeks[weekKey].transactions++;
      }
    });
    return Object.values(weeks).sort((a, b) => a.weekNum - b.weekNum).slice(-12); // Last 12 weeks
  }, [filteredPayments]);

  // Top Repeat Payers
  const topRepeatPayers = useMemo(() => {
    const paymentCounts = {};
    filteredPayments.forEach(p => {
      if (!paymentCounts[p.memberId]) {
        paymentCounts[p.memberId] = { count: 0, total: 0, name: '' };
      }
      paymentCounts[p.memberId].count++;
      paymentCounts[p.memberId].total += parseFloat(p.amount) || 0;
    });
    members.forEach(m => {
      if (paymentCounts[m.id]) {
        paymentCounts[m.id].name = m.name;
      }
    });
    return Object.entries(paymentCounts)
      .map(([id, data]) => ({ id, ...data }))
      .filter(d => d.count > 1)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredPayments, members]);

  // Top Payers by Amount
  const topPayersByAmount = useMemo(() => {
    const paymentTotals = {};
    filteredPayments.forEach(p => {
      if (!paymentTotals[p.memberId]) {
        paymentTotals[p.memberId] = { count: 0, total: 0, name: '' };
      }
      paymentTotals[p.memberId].count++;
      paymentTotals[p.memberId].total += parseFloat(p.amount) || 0;
    });
    members.forEach(m => {
      if (paymentTotals[m.id]) {
        paymentTotals[m.id].name = m.name;
      }
    });
    return Object.entries(paymentTotals)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [filteredPayments, members]);

  // Renewal Insights
  const renewalInsights = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdue = [];
    const warning = [];
    const upcoming = [];
    members.forEach(m => {
      if (m.renewalDue) {
        const dueDate = new Date(m.renewalDue);
        dueDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          overdue.push({ ...m, daysOverdue: Math.abs(diffDays), category: 'overdue' });
        } else if (diffDays <= 14) {
          upcoming.push({ ...m, daysUntil: diffDays, category: 'upcoming' });
        } else if (diffDays <= 30) {
          warning.push({ ...m, daysUntil: diffDays, category: 'warning' });
        }
      }
    });
    return {
      overdue: overdue.sort((a, b) => b.daysOverdue - a.daysOverdue),
      warning: warning.sort((a, b) => a.daysUntil - b.daysUntil),
      upcoming: upcoming.sort((a, b) => a.daysUntil - b.daysUntil),
      all: [...overdue, ...upcoming, ...warning]
    };
  }, [members]);

  // Month-on-Month Renewal Performance (uses payments data)
  const renewalPerformance = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    
    // Count renewals per month from payments (assumption: a payment is a renewal if member has tenure > 1)
    const monthlyRenewals = {};
    const monthlyNewMembers = {};
    
    payments.forEach(p => {
      if (!p.paymentDate) return;
      const date = new Date(p.paymentDate);
      if (date.getFullYear() !== currentYear) return;
      const month = date.getMonth();
      const member = members.find(m => m.id === p.memberId);
      
      if (member && member.tenure && member.tenure > 1) {
        monthlyRenewals[month] = (monthlyRenewals[month] || 0) + 1;
      } else {
        monthlyNewMembers[month] = (monthlyNewMembers[month] || 0) + 1;
      }
    });
    
    return monthNames.map((name, idx) => ({
      month: name,
      renewals: monthlyRenewals[idx] || 0,
      newMembers: monthlyNewMembers[idx] || 0,
      total: (monthlyRenewals[idx] || 0) + (monthlyNewMembers[idx] || 0)
    }));
  }, [payments, members]);

  // Renewal status pie chart data
  const renewalPieData = useMemo(() => {
    const activeMembers = members.filter(m => {
      if (!m.renewalDue) return false;
      const today = new Date();
      const dueDate = new Date(m.renewalDue);
      return dueDate >= today;
    }).length;
    
    return [
      { name: 'Overdue', value: renewalInsights.overdue.length, color: '#f44336' },
      { name: 'Upcoming (0-14d)', value: renewalInsights.upcoming.length, color: '#FF9800' },
      { name: 'Warning (14-30d)', value: renewalInsights.warning.length, color: '#2196F3' },
      { name: 'Active (30d+)', value: Math.max(0, members.length - renewalInsights.all.length), color: '#4CAF50' }
    ].filter(d => d.value > 0);
  }, [members, renewalInsights]);

  // Members by Tenure (Loyal Customers)
  const membersByTenure = useMemo(() => {
    const tenureCounts = {};
    members.forEach(m => {
      const tenure = m.tenure || 1;
      const label = `${tenure} Month${tenure > 1 ? 's' : ''}`;
      if (!tenureCounts[label]) {
        tenureCounts[label] = 0;
      }
      tenureCounts[label]++;
    });
    return Object.entries(tenureCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => parseInt(a.name) - parseInt(b.name));
  }, [members]);

  // Loyal Customers (tenure > 3 months)
  const loyalCustomers = useMemo(() => {
    return members
      .filter(m => m.tenure && m.tenure >= 3)
      .sort((a, b) => b.tenure - a.tenure)
      .slice(0, 15);
  }, [members]);

  // To-Do Items
  const todoItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const items = [];
    members.forEach(m => {
      if (m.renewalDue) {
        const dueDate = new Date(m.renewalDue);
        dueDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays <= 7) {
          items.push({
            type: 'renewal',
            priority: diffDays <= 3 ? 'high' : 'medium',
            member: m,
            message: `${m.name} - Renewal due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
            action: 'Contact for renewal'
          });
        } else if (diffDays < 0 && diffDays >= -14) {
          items.push({
            type: 'overdue',
            priority: 'high',
            member: m,
            message: `${m.name} - Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`,
            action: 'Follow up immediately'
          });
        }
      }
      if (m.tenure === 1) {
        const joinDate = new Date(m.joiningDate);
        const daysSinceJoin = Math.ceil((today - joinDate) / (1000 * 60 * 60 * 24));
        if (daysSinceJoin >= 20 && daysSinceJoin <= 30) {
          items.push({
            type: 'trial',
            priority: 'medium',
            member: m,
            message: `${m.name} - Trial member (${daysSinceJoin} days)`,
            action: 'Convert to regular membership'
          });
        }
      }
    });
    return items.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, [members]);

  const filteredTodoItems = useMemo(() => {
    if (actionFilter === 'all') return todoItems;
    return todoItems.filter(item => item.type === actionFilter);
  }, [todoItems, actionFilter]);

  const filteredRenewalItems = useMemo(() => {
    if (renewalFilter === 'all') return renewalInsights.all;
    return renewalInsights[renewalFilter] || [];
  }, [renewalInsights, renewalFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filterButtonStyle = (isActive) => ({
    padding: '8px 16px',
    border: 'none',
    background: isActive ? '#4CAF50' : '#f0f0f0',
    color: isActive ? 'white' : '#333',
    cursor: 'pointer',
    fontSize: '13px',
    borderRadius: '20px',
    transition: 'all 0.2s'
  });

  // Helper function to generate message from template
  const generateMessage = (template, member, statusText) => {
    // Safety check for undefined template
    if (!template) {
      template = 'Hi {name}, your gym membership is {status}. Please renew to continue your fitness journey!';
    }
    const gymName = gym?.gymName || 'GymPro';
    const expiryDate = member?.renewalDue ? formatDate(member.renewalDue) : '';
    return template
      .replace(/{name}/g, member?.name || '')
      .replace(/{phone}/g, member?.phone || '')
      .replace(/{email}/g, member?.email || '')
      .replace(/{status}/g, statusText || '')
      .replace(/{expiryDate}/g, expiryDate)
      .replace(/{gymName}/g, gymName);
  };

  // Bulk WhatsApp handler - opens multiple tabs
  const handleBulkWhatsApp = (selectedMembers, memberList) => {
    const membersToMessage = memberList.filter(m => selectedMembers.includes(m.id));
    if (membersToMessage.length === 0) {
      alert('Please select at least one member');
      return;
    }
    if (membersToMessage.length > 10) {
      if (!window.confirm(`You are about to open ${membersToMessage.length} WhatsApp tabs. Continue?`)) {
        return;
      }
    }
    membersToMessage.forEach((member, index) => {
      const statusText = member.category === 'overdue' 
        ? `expired ${member.daysOverdue} days ago`
        : member.daysUntil !== undefined 
          ? `expiring in ${member.daysUntil} days`
          : 'expiring soon';
      const message = generateMessage(messageSettings.whatsappTemplate, member, statusText);
      const phone = member.phone?.replace(/\D/g, '');
      const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
      setTimeout(() => window.open(url, '_blank'), index * 500); // Stagger to prevent blocking
    });
  };

  // Bulk Email handler - opens mailto with all emails
  const handleBulkEmail = (selectedMembers, memberList) => {
    const membersToEmail = memberList.filter(m => selectedMembers.includes(m.id) && m.email);
    if (membersToEmail.length === 0) {
      alert('Please select at least one member with an email address');
      return;
    }
    const emails = membersToEmail.map(m => m.email).join(',');
    const gymName = gym?.gymName || 'GymPro';
    const subject = messageSettings.emailSubject.replace(/{gymName}/g, gymName);
    const body = messageSettings.emailTemplate
      .replace(/{name}/g, 'Member')
      .replace(/{status}/g, 'due for renewal')
      .replace(/{gymName}/g, gymName)
      .replace(/{expiryDate}/g, '')
      .replace(/\n/g, '%0A');
    window.location.href = `mailto:${emails}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  // Toggle member selection
  const toggleRenewalMember = (memberId) => {
    setSelectedRenewalMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const toggleActionMember = (memberId) => {
    setSelectedActionMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Select all / Deselect all
  const toggleAllRenewalMembers = () => {
    if (selectedRenewalMembers.length === filteredRenewalItems.length) {
      setSelectedRenewalMembers([]);
    } else {
      setSelectedRenewalMembers(filteredRenewalItems.map(m => m.id));
    }
  };

  const toggleAllActionMembers = () => {
    if (selectedActionMembers.length === filteredTodoItems.length) {
      setSelectedActionMembers([]);
    } else {
      setSelectedActionMembers(filteredTodoItems.map(item => item.member.id));
    }
  };

  // Redirect staff users trying to access revenue section (after all hooks)
  if (section === 'revenue' && !isAdmin) {
    return <Navigate to="/insights/members" replace />;
  }

  if (loading) return <div className="loading">Loading insights...</div>;

  return (
    <div className="insights-page" style={{ padding: '20px' }}>

        {/* REVENUE INSIGHTS */}
        {activeSection === 'revenue' && (
          <>
            <h2 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px', color: themeColors.textPrimary }}>
              💰 Revenue Insights
            </h2>

            {/* Filters */}
            <div style={{
              background: themeColors.cardBg,
              padding: '15px 20px',
              borderRadius: '10px',
              marginBottom: '25px',
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '8px', fontSize: '13px', color: themeColors.textPrimary }}>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '5px', border: `1px solid ${themeColors.borderColor}`, background: themeColors.tableBg, color: themeColors.textPrimary }}>
                  {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '8px', fontSize: '13px', color: themeColors.textPrimary }}>Month:</label>
                <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '5px', border: `1px solid ${themeColors.borderColor}`, background: themeColors.tableBg, color: themeColors.textPrimary }}>
                  <option value="">All</option>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '8px', fontSize: '13px', color: themeColors.textPrimary }}>Payment Mode:</label>
                <select value={selectedPaymentMode} onChange={e => setSelectedPaymentMode(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '5px', border: `1px solid ${themeColors.borderColor}`, background: themeColors.tableBg, color: themeColors.textPrimary }}>
                  <option value="">All</option>
                  {availablePaymentModes.map(mode => <option key={mode} value={mode}>{mode}</option>)}
                </select>
              </div>
              <button onClick={() => { setSelectedYear(new Date().getFullYear()); setSelectedMonth(''); setSelectedPaymentMode(''); }}
                style={{ padding: '6px 12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>
                Reset
              </button>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
              <div style={{ background: 'linear-gradient(135deg, #4CAF50, #45a049)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>₹{filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toLocaleString()}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Total Revenue</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #2196F3, #1976D2)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>{filteredPayments.length}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Transactions</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>₹{filteredPayments.length > 0 ? Math.round(filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) / filteredPayments.length).toLocaleString() : 0}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Avg Transaction</p>
              </div>
            </div>

            {/* Monthly Collection Chart */}
            <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px', marginBottom: '25px' }}>
              <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>📈 Monthly Collection & Transactions ({selectedYear})</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthlyCollectionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                  <XAxis dataKey="month" tick={{ fill: themeColors.chartText }} />
                  <YAxis yAxisId="left" tick={{ fill: themeColors.chartText }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: themeColors.chartText }} />
                  <Tooltip formatter={(value, name) => [name === 'collection' ? `₹${value.toLocaleString()}` : value, name === 'collection' ? 'Collection' : 'Transactions']} contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                  <Legend wrapperStyle={{ color: themeColors.chartText }} />
                  <Bar yAxisId="left" dataKey="collection" fill="#4CAF50" name="Collection (₹)" />
                  <Line yAxisId="right" type="monotone" dataKey="transactions" stroke="#FF9800" strokeWidth={2} name="Transactions" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '25px' }}>
              {/* Revenue by Payment Mode */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>💳 Revenue by Payment Mode</h3>
                {revenueByPaymentMode.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={revenueByPaymentMode}
                        cx="50%"
                        cy="50%"
                        labelLine={{ stroke: themeColors.chartText }}
                        label={({ name, percent, value, x, y, midAngle }) => {
                          const RADIAN = Math.PI / 180;
                          const radius = 120;
                          const cx2 = x + radius * Math.cos(-midAngle * RADIAN) * 0.15;
                          const cy2 = y + radius * Math.sin(-midAngle * RADIAN) * 0.15;
                          return (
                            <text x={cx2} y={cy2} fill={themeColors.chartText} textAnchor={x > 200 ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
                              {`${name}: ₹${value.toLocaleString()} (${(percent * 100).toFixed(0)}%)`}
                            </text>
                          );
                        }}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {revenueByPaymentMode.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: themeColors.textSecondary }}>No payment data available</div>
                )}
              </div>

              {/* Week over Week Revenue */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>📅 Week over Week Revenue</h3>
                {weekOverWeekRevenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={weekOverWeekRevenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: themeColors.chartText }} />
                      <YAxis yAxisId="left" tick={{ fill: themeColors.chartText }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: themeColors.chartText }} />
                      <Tooltip formatter={(value, name) => [name === 'revenue' ? `₹${value.toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Transactions']} contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                      <Legend wrapperStyle={{ color: themeColors.chartText }} />
                      <Bar yAxisId="left" dataKey="revenue" fill="#2196F3" name="Revenue (₹)" />
                      <Line yAxisId="right" type="monotone" dataKey="transactions" stroke="#E91E63" strokeWidth={2} dot={{ fill: '#E91E63' }} name="Transactions" />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: themeColors.textSecondary }}>No weekly data available</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* MEMBER INSIGHTS */}
        {activeSection === 'members' && (
          <>
            <h2 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px', color: themeColors.textPrimary }}>
              👥 Member Insights
            </h2>

            {/* Filters */}
            <div style={{
              background: themeColors.cardBg,
              padding: '15px 20px',
              borderRadius: '10px',
              marginBottom: '25px',
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '8px', fontSize: '13px', color: themeColors.textPrimary }}>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '5px', border: `1px solid ${themeColors.borderColor}`, background: themeColors.tableBg, color: themeColors.textPrimary }}>
                  {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 'bold', marginRight: '8px', fontSize: '13px', color: themeColors.textPrimary }}>Tenure:</label>
                <select value={selectedTenure} onChange={e => setSelectedTenure(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '5px', border: `1px solid ${themeColors.borderColor}`, background: themeColors.tableBg, color: themeColors.textPrimary }}>
                  <option value="">All</option>
                  {availableTenures.map(t => <option key={t} value={t}>{t} Month{t > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
              <div style={{ background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>{members.length}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Total Members</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #00BCD4, #0097A7)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>{loyalCustomers.length}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Loyal (3+ months)</p>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #E91E63, #C2185B)', color: 'white', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '28px' }}>{topRepeatPayers.length}</h3>
                <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Repeat Payers</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px', marginBottom: '25px' }}>
              {/* Monthly Members Chart */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>📈 Monthly New Members ({selectedYear})</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyUsersData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                    <XAxis dataKey="month" tick={{ fill: themeColors.chartText }} />
                    <YAxis tick={{ fill: themeColors.chartText }} />
                    <Tooltip contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                    <Bar dataKey="members" fill="#9C27B0" name="New Members" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Top Repeat Payers */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>🔄 Top Repeat Payers</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topRepeatPayers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                    <XAxis type="number" tick={{ fill: themeColors.chartText }} />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12, fill: themeColors.chartText }} />
                    <Tooltip formatter={(value) => [`${value} payments`, 'Payments']} contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                    <Bar dataKey="count" fill="#00BCD4" name="Payment Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
              {/* Members by Tenure - Loyal Customers */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>🏆 Member Tenure Distribution (Loyalty)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={membersByTenure}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent, x, y }) => (
                        <text x={x} y={y} fill={themeColors.chartText} textAnchor="middle" dominantBaseline="central" fontSize={12}>
                          {`${name} (${(percent * 100).toFixed(0)}%)`}
                        </text>
                      )}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {membersByTenure.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Top Members by Payment */}
              <div style={{ background: themeColors.cardBg, padding: '20px', borderRadius: '10px' }}>
                <h3 style={{ marginTop: 0, color: themeColors.textPrimary }}>💎 Top Members by Payment</h3>
                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: themeColors.tableHeaderBg }}>
                        <th style={{ padding: '10px', textAlign: 'left', color: themeColors.textPrimary }}>#</th>
                        <th style={{ padding: '10px', textAlign: 'left', color: themeColors.textPrimary }}>Member</th>
                        <th style={{ padding: '10px', textAlign: 'right', color: themeColors.textPrimary }}>Payments</th>
                        <th style={{ padding: '10px', textAlign: 'right', color: themeColors.textPrimary }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPayersByAmount.map((p, i) => (
                        <tr key={p.id} style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
                          <td style={{ padding: '10px', color: themeColors.textPrimary }}>#{i + 1}</td>
                          <td style={{ padding: '10px', color: themeColors.textPrimary }}>{p.name || 'Unknown'}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: themeColors.textPrimary }}>{p.count}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#4CAF50', fontWeight: 'bold' }}>₹{p.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* RENEWAL INSIGHTS - Charts Only */}
        {activeSection === 'renewals' && (
          <>
            <h2 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px', color: themeColors.textPrimary }}>
              🔔 Renewal Insights
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: themeColors.textSecondary }}>
                (For actions, go to Follow-up section)
              </span>
            </h2>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#ffebee', padding: '20px', borderRadius: '10px', textAlign: 'center', border: '2px solid #f44336' }}>
                <h3 style={{ margin: 0, fontSize: '36px', color: '#c62828' }}>{renewalInsights.overdue.length}</h3>
                <p style={{ margin: '5px 0 0', color: '#c62828', fontWeight: 'bold', fontSize: '14px' }}>🚨 Overdue</p>
              </div>
              <div style={{ background: '#fff3e0', padding: '20px', borderRadius: '10px', textAlign: 'center', border: '2px solid #FF9800' }}>
                <h3 style={{ margin: 0, fontSize: '36px', color: '#e65100' }}>{renewalInsights.upcoming.length}</h3>
                <p style={{ margin: '5px 0 0', color: '#e65100', fontWeight: 'bold', fontSize: '14px' }}>⏰ Upcoming (0-14d)</p>
              </div>
              <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '10px', textAlign: 'center', border: '2px solid #2196F3' }}>
                <h3 style={{ margin: 0, fontSize: '36px', color: '#1565c0' }}>{renewalInsights.warning.length}</h3>
                <p style={{ margin: '5px 0 0', color: '#1565c0', fontWeight: 'bold', fontSize: '14px' }}>⚠️ Warning (14-30d)</p>
              </div>
              <div style={{ background: '#e8f5e9', padding: '20px', borderRadius: '10px', textAlign: 'center', border: '2px solid #4CAF50' }}>
                <h3 style={{ margin: 0, fontSize: '36px', color: '#2e7d32' }}>{Math.max(0, members.length - renewalInsights.all.length)}</h3>
                <p style={{ margin: '5px 0 0', color: '#2e7d32', fontWeight: 'bold', fontSize: '14px' }}>✅ Active (30d+)</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
              
              {/* Renewal Status Pie Chart */}
              <div style={{ background: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: themeColors.textPrimary }}>📊 Membership Status Distribution</h3>
                {renewalPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={renewalPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent, x, y }) => (
                          <text x={x} y={y} fill={themeColors.chartText} textAnchor="middle" dominantBaseline="central" fontSize={12}>
                            {`${name}: ${(percent * 100).toFixed(0)}%`}
                          </text>
                        )}
                      >
                        {renewalPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: themeColors.textMuted }}>No data available</div>
                )}
              </div>

              {/* Month-on-Month Renewals vs New Members */}
              <div style={{ background: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: themeColors.textPrimary }}>📈 Renewals vs New Members ({new Date().getFullYear()})</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={renewalPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGrid} />
                    <XAxis dataKey="month" tick={{ fill: themeColors.chartText }} />
                    <YAxis tick={{ fill: themeColors.chartText }} />
                    <Tooltip contentStyle={{ backgroundColor: themeColors.tooltipBg, borderColor: themeColors.borderColor, color: themeColors.textPrimary }} />
                    <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
                    <Bar dataKey="renewals" name="Renewals" fill="#4CAF50" />
                    <Bar dataKey="newMembers" name="New Members" fill="#2196F3" />
                    <Line type="monotone" dataKey="total" name="Total" stroke="#FF9800" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Renewal Health Score */}
              <div style={{ background: themeColors.cardBg, borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', color: themeColors.textPrimary }}>💪 Renewal Health Score</h3>
                {(() => {
                  const total = members.length || 1;
                  const overduePercent = (renewalInsights.overdue.length / total * 100).toFixed(1);
                  const activePercent = ((total - renewalInsights.all.length) / total * 100).toFixed(1);
                  const healthScore = Math.max(0, 100 - (overduePercent * 2) - (renewalInsights.upcoming.length / total * 50));
                  
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: '150px', 
                        height: '150px', 
                        borderRadius: '50%', 
                        background: `conic-gradient(${healthScore >= 70 ? '#4CAF50' : healthScore >= 40 ? '#FF9800' : '#f44336'} ${healthScore}%, #e0e0e0 0)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '20px auto'
                      }}>
                        <div style={{ 
                          width: '120px', 
                          height: '120px', 
                          borderRadius: '50%', 
                          background: themeColors.cardBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <div style={{ fontSize: '32px', fontWeight: 'bold', color: healthScore >= 70 ? '#4CAF50' : healthScore >= 40 ? '#FF9800' : '#f44336' }}>
                            {healthScore.toFixed(0)}%
                          </div>
                          <div style={{ fontSize: '12px', color: themeColors.textSecondary }}>Health Score</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '20px' }}>
                        <div style={{ padding: '10px', background: '#e8f5e9', borderRadius: '8px' }}>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2e7d32' }}>{activePercent}%</div>
                          <div style={{ fontSize: '12px', color: themeColors.textSecondary }}>Active Members</div>
                        </div>
                        <div style={{ padding: '10px', background: '#ffebee', borderRadius: '8px' }}>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#c62828' }}>{overduePercent}%</div>
                          <div style={{ fontSize: '12px', color: themeColors.textSecondary }}>Overdue Rate</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

            </div>
          </>
        )}

        {/* ACTION ITEMS - Redirected to Follow-up */}
        {activeSection === 'actions' && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📋</div>
            <h2 style={{ marginBottom: '10px', color: themeColors.textPrimary }}>Action Items have moved!</h2>
            <p style={{ color: themeColors.textSecondary, marginBottom: '30px' }}>
              All action items are now in the dedicated <strong>Follow-up</strong> section for better organization.
            </p>
            <a 
              href="/followup" 
              style={{
                display: 'inline-block',
                padding: '15px 30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              Go to Follow-up Center →
            </a>
          </div>
        )}

      {/* Follow-Up Modal */}
      {showFollowUpModal && followUpMember && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowFollowUpModal(false)}>
          <div style={{
            background: themeColors.cardBg,
            padding: '25px',
            borderRadius: '15px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px', color: themeColors.textPrimary }}>
              📝 Log Follow-Up
            </h3>
            
            <div style={{ background: themeColors.tableHeaderBg, padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', color: themeColors.textPrimary }}>{followUpMember.name}</div>
              <div style={{ fontSize: '13px', color: themeColors.textSecondary }}>📞 {followUpMember.phone}</div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Contact Date</label>
              <input
                type="date"
                value={followUpData.contactDate}
                onChange={e => setFollowUpData({ ...followUpData, contactDate: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Contact Mode</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {['WhatsApp', 'Call', 'Email', 'In-Person'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setFollowUpData({ ...followUpData, contactMode: mode })}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      background: followUpData.contactMode === mode ? 
                        (mode === 'WhatsApp' ? '#25D366' : mode === 'Call' ? '#2196F3' : mode === 'Email' ? '#FF9800' : '#9C27B0') 
                        : '#f0f0f0',
                      color: followUpData.contactMode === mode ? 'white' : '#333'
                    }}
                  >
                    {mode === 'WhatsApp' ? '💬' : mode === 'Call' ? '📞' : mode === 'Email' ? '✉️' : '🏋️'} {mode}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Outcome</label>
              <select
                value={followUpData.outcome}
                onChange={e => setFollowUpData({ ...followUpData, outcome: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}
              >
                <option value="">Select outcome...</option>
                <option value="Renewed">✅ Renewed</option>
                <option value="Promised">🤝 Promised to renew</option>
                <option value="Thinking">🤔 Thinking about it</option>
                <option value="No Response">📵 No Response</option>
                <option value="Declined">❌ Declined</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Notes</label>
              <textarea
                value={followUpData.notes}
                onChange={e => setFollowUpData({ ...followUpData, notes: e.target.value })}
                rows={3}
                placeholder="Add any notes about the conversation..."
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowFollowUpModal(false)}
                style={{ padding: '10px 20px', border: '1px solid #ddd', borderRadius: '8px', background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={submitFollowUp}
                style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: '#4CAF50', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Save Follow-Up
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Insights;
